import type { ModifyUrlChangeListeners, NavigationParams, IUrlHistoryState } from '@wix/thunderbolt-symbols'
import {
	getFullUrlWithoutQueryParams as getFullUrlWithoutQueryParamsOf,
	getRelativeEncodedUrl,
	getRelativeUrl,
	isAddressRoutedAs,
	toRoutableUrl,
} from './utils'
import type { UrlManagerFactoryApi, UrlManagerParams } from './types'
import { applyRetainedQueryParams, isSafari } from '@wix/thunderbolt-commons'

export const createUrlHistoryManager = ({
	browserWindow,
	onUrlChangeListeners,
	requestUrl,
	externalBaseUrl,
	proxyBasePath,
	currentRouteInfo,
	logger,
	isUpdateHrefInSafariIncognitoOpen,
}: UrlManagerParams): UrlManagerFactoryApi => {
	const state: { previousPageId?: string } = {}
	let urlChangeListeners = [...onUrlChangeListeners]

	// A third-party app may monkey-patch history.replaceState and throw; don't let that abort navigation.
	const safeReplaceState = (data: unknown, url: string): boolean => {
		try {
			browserWindow!.history.replaceState(data, '', url)
			return true
		} catch (ex) {
			logger.captureError(ex as Error, { tags: { feature: 'feature-router', replaceStateError: true } })
			// A throwing patch may still have applied the url before throwing; treat as applied
			// only if location actually reached the target, so listeners aren't wrongly skipped.
			return browserWindow!.location.href === url
		}
	}

	const getCurrentUrl = (): string => browserWindow?.location.href || requestUrl
	// The page a proxied address shows - the url to derive the current page from (the address itself stays in history)
	const getRoutedUrl = () => toRoutableUrl(getCurrentUrl(), proxyBasePath, requestUrl)
	const getRelativeUrlInternal = () => getRelativeUrl(getRoutedUrl(), externalBaseUrl)
	const getFullUrlWithoutQueryParams = () => getFullUrlWithoutQueryParamsOf(getRoutedUrl(), externalBaseUrl)
	const pushUrl = (
		_parsedUrl: URL,
		{ disableScrollToTop, skipHistory }: Omit<NavigationParams, 'anchorDataId'> = {}
	) => {
		if (!browserWindow || !browserWindow.history) {
			return
		}

		const currentUrl = new URL(browserWindow.location.href)
		// Keep a proxied address that already shows the target page
		if (isAddressRoutedAs(currentUrl, _parsedUrl, proxyBasePath, requestUrl)) {
			return
		}

		const parsedUrl = applyRetainedQueryParams(currentUrl, _parsedUrl.href)
		const url = parsedUrl.toString()

		parsedUrl.searchParams.sort()
		currentUrl.searchParams.sort()

		const historyState: IUrlHistoryState = { scrollY: browserWindow.scrollY }
		if (skipHistory) {
			// skipHistory applies `url` via this replaceState; if it fails, the url never took effect,
			// so bail out rather than notify listeners about a URL that isn't reflected in location.
			if (!safeReplaceState(historyState, url)) {
				return
			}
		}

		if (currentUrl.toString() === parsedUrl.toString()) {
			return
		}

		if (!skipHistory) {
			safeReplaceState(historyState, currentUrl.toString())
			try {
				const data = disableScrollToTop ? { scrollY: historyState?.scrollY } : null
				browserWindow.history.pushState(data, '', url)

				// Safari in incognito + reaching a site indirectly (from google for example) doesn't update window.location.href correctly.
				// Query params are not updated
				if (isUpdateHrefInSafariIncognitoOpen && isSafari(window) && url !== window.location.href) {
					window.location.href = url
				}
			} catch (ex) {
				logger.captureError(ex, { tags: { feature: 'feature-router', pushStateError: true } })
			}
		}

		const currentPageId = currentRouteInfo.getCurrentRouteInfo()?.pageId

		if (state.previousPageId === currentPageId) {
			urlChangeListeners.forEach((listener) => listener.onUrlChange(new URL(url)))
		}
	}

	const addOnUrlChangeListener: ModifyUrlChangeListeners = (listenerToAdd) => urlChangeListeners.push(listenerToAdd)
	const removeOnUrlChangeListener: ModifyUrlChangeListeners = (listenerToRemove) =>
		(urlChangeListeners = urlChangeListeners.filter((listener) => listener !== listenerToRemove))

	const api = {
		getHistoryState: () => {
			if (!browserWindow || !browserWindow.history) {
				return null
			}
			return browserWindow.history.state as IUrlHistoryState
		},

		updateHistoryState: (newHistory?: IUrlHistoryState) => {
			if (!browserWindow || !browserWindow.history) {
				return
			}

			if (newHistory) {
				const currentUrl = new URL(browserWindow.location.href)
				currentUrl.searchParams.sort()

				safeReplaceState(newHistory, currentUrl.toString())
			}

			return
		},

		getParsedUrl: () => new URL(getCurrentUrl()),

		getFullUrlWithoutQueryParams,

		getRelativeUrl: getRelativeUrlInternal,

		getRelativeEncodedUrl: () => getRelativeEncodedUrl(getRoutedUrl(), externalBaseUrl),
	}

	const updateState = (pageId: string) => {
		state.previousPageId = pageId
	}

	return { api, addOnUrlChangeListener, removeOnUrlChangeListener, pushUrl, getCurrentUrl, updateState }
}
